/**
 * Every button in the app.
 *
 * The classes are the ones the stylesheet already themes for v1, v2 and v3 —
 * this component adds no styling of its own. What it adds is a single place
 * where "what kind of button is this" is written down, so call sites stop
 * hand-assembling class strings and the set of kinds stays knowable.
 *
 * `variant` picks the family. `active` applies that family's own on-state
 * class, which differs between them (.active, --on, --copied) and was being
 * spelled out by hand at every call site.
 */
const VARIANTS = {
  /* The .btn family — the ordinary buttons on forms and cards. */
  primary: 'btn',
  outline: 'btn btn-outline',
  red: 'btn btn-red',
  green: 'btn btn-green',

  /* Filter chips. Three families, because the three themes style them
     differently and always have. */
  chip: 'v3-ops-chip',      // V3 operations / archive / log
  filter: 'camp-chip',      // V1/V2 campaigns
  metric: 'cu-metric',      // Custom report metric pills
  segment: 'cu-gran__opt',  // Custom report segmented control

  /* Chrome. */
  ctrl: 'v3-ctrl-btn',              // V3 ticker controls
  eject: 'v3-ctrl-btn v3-eject',    // …and the sign-out one
  toggle: 'topbar-toggle',          // V1/V2 topbar toggles
  token: 'topbar-token',            // copy-token, which turns green when copied
  logout: 'logout-btn',
  dismiss: 'upd-btn',               // what's-new banner
  cta: 'onboard__cta',              // first-run guide
  skip: 'hax-skip--on',             // boot sequence

  /* Controls that live inside a row or a card. */
  unit: 'v3-unit-ctrl',     // V3 campaign card pause / budget / ads
  expand: 'camp-expand',    // the ▸/▾ disclosure in a campaigns row
  help: 'arch-help__btn',   // the ⓘ that opens the archive guide
  day: 'wk-day',            // a column of the week chart
  name: 'arch-name',        // a table cell that opens a detail view
  bulk: 'notif-prefs__bulk', // the "4 of 7" group control in notification prefs
  link: 'link-button',      // reads as a link, behaves as a button
  reveal: 'pw-toggle',      // show/hide password

  /* No class at all: the week arrows and the row expander, which the
     stylesheet reaches through their parent. */
  bare: '',
};

/** The on-state class per family — they genuinely differ. */
const ACTIVE = {
  chip: 'active',
  filter: 'camp-chip--on',
  metric: 'cu-metric--on',
  segment: 'cu-gran__opt--on',
  toggle: 'topbar-toggle--on',
  token: 'topbar-token--copied',
  day: 'wk-day--today',
};

/**
 * Variants whose `active` really means "this toggle is on", so the on-state is
 * worth announcing. They get `aria-pressed` for free.
 *
 * Deliberately NOT every variant that accepts `active`: `token` goes green for a
 * moment after a copy, which is a transient confirmation rather than a state a
 * screen reader should report as pressed.
 */
const TOGGLES = new Set(['filter', 'chip', 'metric', 'segment', 'toggle']);

export default function Button(props) {
  const {
    variant = 'primary',
    size,                    // 'sm' → .btn-sm
    full,                    // .btn-full
    active = false,
    className = '',
    type = 'button',
    children,
    ...rest
  } = props;

  const base = VARIANTS[variant] ?? VARIANTS.primary;
  const classes = [
    base,
    active ? ACTIVE[variant] : '',
    size === 'sm' && base.startsWith('btn') ? 'btn-sm' : '',
    full ? 'btn-full' : '',
    className,
  ].filter(Boolean).join(' ');

  /*
   * Gated on `active` being SUPPLIED, not merely on the variant.
   *
   * These variants carry plenty of buttons that are not toggles — ↻ REFRESH and
   * CANCEL on V3, the three-way THEME: cycle — and because `active` defaults to
   * false, keying off the variant alone stamped aria-pressed="false" on all of
   * them. A screen reader then announces a plain action button as an unpressed
   * toggle, which is worse than saying nothing.
   *
   * `rest` is spread AFTER, so a call site passing its own aria-pressed wins.
   */
  const pressed = TOGGLES.has(variant) && 'active' in props ? { 'aria-pressed': active } : null;

  return <button type={type} className={classes || undefined} {...pressed} {...rest}>{children}</button>;
}
